import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Building2 } from "lucide-react";
import SeoHead, { orgSchema, personSchema, breadcrumbSchema } from "@/components/SeoHead";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import LeadModal from "@/components/LeadModal";
import { SITE, MARK } from "@/lib/seoData";

const profilePageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ProfilePage",
  "@id": `${SITE.url}${MARK.path}#profilepage`,
  url: `${SITE.url}${MARK.path}`,
  name: `${MARK.name} — ${MARK.role}, ${MARK.org}`,
  mainEntity: { "@id": MARK.personId },
  isPartOf: { "@id": `${SITE.url}/#website` },
});

export default function MarkShaikh() {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-black text-white min-h-screen">
      <SeoHead
        title="Mark Shaikh — Founder & Director, OzyLoans"
        description="Mark Shaikh is Founder & Director of OzyLoans — an accredited finance broker since 2006 and MFAA member with experience across commercial, development, bridging and private-credit finance in Australia."
        path={MARK.path}
        schemas={[
          orgSchema(),
          personSchema({
            name: MARK.name,
            jobTitle: MARK.role,
            path: MARK.path,
            knowsAbout: MARK.areas,
          }),
          profilePageSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
            { name: MARK.name, path: MARK.path },
          ]),
        ]}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="pt-24 pb-14 sm:pt-32 sm:pb-20 px-6 border-b border-zinc-900" data-testid="mark-hero">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.4em] text-[#E50914] mb-5">Author · Expert profile</p>
          <h1 className="font-bebas text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight text-white">
            Mark Shaikh — <span className="text-[#E50914]">Founder &amp; Director</span>, OzyLoans
          </h1>
        </div>
      </section>

      {/* PROFESSIONAL OVERVIEW */}
      <section className="py-16 sm:py-24 px-6" data-testid="mark-overview">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-bebas text-3xl sm:text-4xl tracking-tight mb-6">Professional overview</h2>
          <div className="space-y-5 text-zinc-300 text-base sm:text-lg leading-relaxed">
            {MARK.bio.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
          </div>
        </div>
      </section>

      {/* AREAS OF EXPERIENCE */}
      <section className="py-16 sm:py-24 px-6 border-t border-zinc-900 bg-[#0a0a0a]" data-testid="mark-areas">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-bebas text-3xl sm:text-4xl tracking-tight mb-8">Areas of finance experience</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {MARK.areas.map((a) => (
              <div key={a} className="flex items-center gap-3 bg-[#0c0c0c] border border-zinc-800/70 rounded-xl px-5 py-4" data-testid={`mark-area-${a.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                <Building2 className="w-5 h-5 text-[#E50914] shrink-0" />
                <span className="text-zinc-200">{a}</span>
              </div>
            ))}
          </div>
          <p className="text-zinc-400 text-sm mt-6 leading-relaxed">
            Includes commercial private credit and{" "}
            <Link to="/finance/private-lender-exit" className="text-[#E50914] underline underline-offset-4 decoration-[#E50914]/40 hover:text-white transition-colors">private lender exit finance</Link>{" "}
            for borrowers refinancing maturing short-term facilities.
          </p>
        </div>
      </section>

      {/* PROFESSIONAL BACKGROUND */}
      <section className="py-16 sm:py-24 px-6 border-t border-zinc-900" data-testid="mark-background">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-bebas text-3xl sm:text-4xl tracking-tight mb-8">Professional background</h2>
          <ul className="space-y-4">
            {MARK.credentials.map((c) => (
              <li key={c} className="flex items-start gap-3 text-zinc-300">
                <Award className="w-5 h-5 text-[#E50914] shrink-0 mt-0.5" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* INSIGHTS BY MARK */}
      <section className="py-16 sm:py-24 px-6 border-t border-zinc-900 bg-[#0a0a0a]" data-testid="mark-insights">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-bebas text-3xl sm:text-4xl tracking-tight mb-6">Insights by Mark Shaikh</h2>
          <div className="rounded-2xl border border-zinc-800 bg-[#0c0c0c] p-8 text-center" data-testid="mark-insights-empty">
            <p className="text-zinc-400">Expert insights coming soon.</p>
            <Link to="/insights" className="inline-flex items-center gap-1.5 text-[#E50914] hover:text-white text-sm mt-3 transition-colors">
              Browse all insights <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* OZYLOANS RELATIONSHIP */}
      <section className="py-16 sm:py-24 px-6 border-t border-zinc-900" data-testid="mark-relationship">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-bebas text-3xl sm:text-4xl tracking-tight mb-6">About OzyLoans</h2>
          <p className="text-zinc-300 text-base sm:text-lg leading-relaxed">
            Mark is Founder &amp; Director of{" "}
            <Link to="/about" className="text-[#E50914] underline underline-offset-4 decoration-[#E50914]/40 hover:text-white transition-colors">OzyLoans</Link>, an Australian commercial credit platform specialising in{" "}
            <Link to="/finance/private-credit" className="text-[#E50914] underline underline-offset-4 decoration-[#E50914]/40 hover:text-white transition-colors">commercial private credit</Link>, development, bridging and refinance solutions.
          </p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="mark-cta-button"
            className="h-13 px-7 py-3 rounded-full bg-[#E50914] hover:bg-[#F6121D] text-white font-semibold text-base inline-flex items-center gap-2 mt-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          >
            Start a funding conversation <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <SiteFooter />
      <LeadModal open={open} onOpenChange={setOpen} sourceChannel="about_page" />
    </div>
  );
}
